import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

/**
 * Tracks which download is in flight, so a button can say so.
 *
 * The gated downloads fetch the whole archive into memory before the browser
 * is handed anything to save — they have to, because the request needs
 * credentials and the response may be a 403 to be read rather than a file. For
 * the framework that is 140-170 MB streamed through our server, which is
 * several seconds of nothing happening after the click. People click again.
 *
 * So the click is acknowledged immediately, twice over: a toast that stays up
 * for the duration, and a busy state the button uses to disable itself. Both
 * clear when the save is handed off.
 *
 * Keyed rather than a single boolean because a page can offer several
 * downloads — a platform per version on the changelog — and only the one that
 * was clicked should look busy.
 */
export function useDownloadTracker() {
  const { t } = useTranslation();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // A ref as well as state: state updates are async, so two fast clicks can
  // both read the old value and start two downloads before either render.
  const inFlight = useRef<string | null>(null);

  const run = useCallback(
    async (key: string, download: () => Promise<void>) => {
      // Any download in flight, not just this key: these are large, and
      // running several at once makes all of them slower and the page a
      // liar about which is happening.
      if (inFlight.current) return;
      inFlight.current = key;
      setBusyKey(key);

      // An id, so this exact toast is replaced rather than stacking up
      // behind whatever else is on screen.
      const toastId = `download-${key}`;
      toast.loading(t('download.inProgress'), { id: toastId });

      try {
        await download();
        // The handler owns the success message: only it knows whether the
        // bytes arrived or the server refused with something worth saying.
        toast.dismiss(toastId);
      } catch {
        toast.error(t('download.failed'), { id: toastId });
      } finally {
        inFlight.current = null;
        setBusyKey(null);
      }
    },
    [t]
  );

  return {
    /** The key currently downloading, or null. */
    busyKey,
    /** True while any download is in flight. */
    isDownloading: busyKey !== null,
    run
  };
}
